<template>
  <div>
    <v-combobox
      :label="label"
      :value="value"
      @input="handleInput"
      :items="list"
      :clearable="!readonly && clearable"
      :readonly="readonly"
      :loading="loading"
      hide-details="auto"
      v-bind="$attrs"
      :prepend-inner-icon="icon || undefined"
      :search-input.sync="search"
      @update:search-input="handleSearchInput"
      :class="{ 'small-input': small }"
      :background-color="$vuetify.theme.themes.light.inputBackgroundColor"
      :item-text="itemText"
      :item-value="itemValue"
      return-object
      flat
      outlined
    >
      <!-- Pass through all slots -->
      <template v-for="(_, name) in $scopedSlots" v-slot:[name]="slotData">
        <slot :name="name" v-bind="slotData" />
      </template>
    </v-combobox>
  </div>
</template>

<script>
import { crudList } from '@/api/crud';
import api from '@/api';
import { debounce, isEqual } from 'lodash';

export default {
  name: 'SearchableCombobox',
  props: {
    path: {
      type: String,
      required: true,
    },
    value: {},
    params: {
      type: Object,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    icon: {
      type: String,
      default: '',
    },
    small: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    label: {
      type: String,
      default: '',
    },
    itemText: {
      type: String,
      default: 'name',
    },
    itemValue: {
      type: String,
      default: '_id',
    },
  },
  data() {
    return {
      list: [],
      search: null,
      loading: false,
      debounceToGetData: debounce(this.reload, 500),
    };
  },
  methods: {
    async reload() {
      if (!this.search || this.search.length < 2) {
        this.list = [];
        return;
      }

      this.loading = true;
      try {
        const searchParams = {
          search: this.search,
          ...this.params,
        };

        const searchResult = await crudList(api, this.path, searchParams);
        this.list = searchResult.docs || searchResult;
      } catch (error) {
        console.error('Search failed:', error);
        this.list = [];
      } finally {
        this.loading = false;
      }
    },
    handleInput(selectedItem) {
      // If an item from the list is selected (object), extract the string value
      if (selectedItem && typeof selectedItem === 'object') {
        const stringValue = selectedItem[this.itemText] || '';
        this.$emit('input', stringValue);
        this.$emit('selectItem', selectedItem);
      } else {
        // User is just typing, emit the string as-is
        this.$emit('input', selectedItem);
      }
    },
    handleSearchInput(searchValue) {
      // Emit typed value immediately to keep v-model in sync while typing
      if (typeof searchValue === 'string' && searchValue !== this.value) {
        this.$emit('input', searchValue);
      }
    },
  },
  watch: {
    search: {
      immediate: false,
      handler(v) {
        this.debounceToGetData();
        // Remove any emit from here since handleSearchInput now handles it
      },
    },
    params: {
      handler(v, oldV) {
        if (!isEqual(v, oldV)) {
          this.debounceToGetData();
        }
      },
      deep: true,
    },
  },
};
</script>

<style lang="scss" scoped>
.small-input {
  font-size: 0.875rem;
}
</style>
